import { useAppTheme } from "@hooks/useAppTheme";
import { useDevice } from "@hooks/useDevice";
import type { FlashcardEntry } from "@memorang/types/src/session/new-session";
import { Box, MarkdownRenderer, ZoomImage } from "@memorang/ui";
import CustomTabs from "components/CustomTabs";
import { useEffect, useMemo, useState } from "react";
import { Pressable, ScrollView, View, useWindowDimensions } from "react-native";
import { Button, Caption, Icon, Text } from "react-native-paper";
import Animated, {
	interpolate,
	useAnimatedStyle,
	useSharedValue,
	withTiming,
} from "react-native-reanimated";

interface Props {
	cards: FlashcardEntry[];
	itemId: number;
	onAllViewed?: () => void;
}

type Section = { title: string; content: string };

const parseSections = (back: string): Section[] | null => {
	const analysisMatch = back.match(/^ANALYSIS\s*\n/m);
	const questionsMatch = back.match(/^QUESTIONS\s*\n/m);

	const questionsOffset = questionsMatch
		? (questionsMatch.index ?? 0) + questionsMatch[0].length
		: 0;
	const afterQuestions = back.slice(questionsOffset);
	const takeawayLocal = afterQuestions.match(/^KEY TAKEAWAY\s*\n/m);
	const takeawayIndex =
		takeawayLocal != null ? questionsOffset + (takeawayLocal.index ?? 0) : -1;
	const takeawayStart =
		takeawayLocal != null
			? questionsOffset + (takeawayLocal.index ?? 0) + takeawayLocal[0].length
			: -1;

	const found = [analysisMatch, questionsMatch, takeawayLocal].filter(
		Boolean,
	).length;
	if (found < 2) return null;

	const sections: Section[] = [];

	if (analysisMatch) {
		const start = (analysisMatch.index ?? 0) + analysisMatch[0].length;
		const end =
			questionsMatch?.index ??
			(takeawayIndex >= 0 ? takeawayIndex : back.length);
		sections.push({
			title: "Analysis",
			content: back.slice(start, end).trim(),
		});
	}

	if (questionsMatch) {
		const start = (questionsMatch.index ?? 0) + questionsMatch[0].length;
		const end = takeawayIndex >= 0 ? takeawayIndex : back.length;
		sections.push({
			title: "Questions",
			content: back.slice(start, end).trim(),
		});
	}

	if (takeawayStart >= 0) {
		sections.push({
			title: "Key Takeaway",
			content: back.slice(takeawayStart).trim(),
		});
	}

	return sections;
};

const FlipCard = ({
	card,
	cards,
	cardIndex,
	cardWidth,
	cardHeight,
}: {
	card: FlashcardEntry;
	cards: FlashcardEntry[];
	cardIndex: number;
	cardWidth: number;
	cardHeight: number;
}) => {
	const theme = useAppTheme();
	const flipped = useSharedValue(0);
	const [isFlipped, setIsFlipped] = useState(false);

	const frontStyle = useAnimatedStyle(() => ({
		transform: [
			{ perspective: 1000 },
			{ rotateY: `${interpolate(flipped.value, [0, 1], [0, 180])}deg` },
		],
		opacity: flipped.value < 0.5 ? 1 : 0,
	}));
	const backStyle = useAnimatedStyle(() => ({
		transform: [
			{ perspective: 1000 },
			{ rotateY: `${interpolate(flipped.value, [0, 1], [180, 360])}deg` },
		],
		opacity: flipped.value >= 0.5 ? 1 : 0,
	}));

	const handleFlip = () => {
		const next = flipped.value === 0 ? 1 : 0;
		flipped.value = withTiming(next, { duration: 350 });
		setIsFlipped(next === 1);
	};

	const baseFaceStyle = {
		position: "absolute" as const,
		width: cardWidth,
		height: cardHeight,
		backfaceVisibility: "hidden" as const,
		borderRadius: 16,
		borderWidth: 1,
		borderColor: theme.colors.outlineVariant,
		backgroundColor: theme.colors.surface,
		overflow: "hidden" as const,
	};

	const allImages = useMemo(
		() =>
			cards
				.filter((c) => !!c.front.image)
				.map((c) => ({ url: c.front.image! })),
		[cards],
	);

	const initialZoomIndex = useMemo(() => {
		if (!card.front.image) {
			return 0;
		}
		return cards
			.filter((c) => !!c.front.image)
			.findIndex((c) => c.front.image === card.front.image);
	}, [card.front.image, cards]);

	const imageWidth = Math.min(cardWidth - 48, 160);
	const imageHeight = Math.round(imageWidth * (4 / 3));
	const sections = useMemo(() => parseSections(card.back), [card.back]);

	return (
		<View style={{ width: cardWidth, height: cardHeight }}>
			<Animated.View
				style={[baseFaceStyle, frontStyle]}
				accessibilityElementsHidden={isFlipped}
				importantForAccessibility={isFlipped ? "no-hide-descendants" : "auto"}
			>
				<Pressable
					onPress={handleFlip}
					style={{ flex: 1 }}
					accessibilityRole="button"
					accessibilityLabel={card.front.text}
					accessibilityHint="Double tap to flip"
				>
					<ScrollView
						style={{ flex: 1 }}
						contentContainerStyle={{
							paddingHorizontal: 20,
							paddingVertical: 16,
							alignItems: "center",
							justifyContent: "center",
							flexGrow: 1,
							gap: 16,
						}}
						showsVerticalScrollIndicator
					>
						{card.front.image ? (
							<ZoomImage
								contentFit="contain"
								url={card.front.image}
								allImages={allImages}
								initialIndex={initialZoomIndex < 0 ? 0 : initialZoomIndex}
								width={imageWidth}
								height={imageHeight}
								borderRadius={12}
							/>
						) : null}
						<Box gap={4} alignItems="center">
							<Text
								variant="titleMedium"
								style={{ textAlign: "center", color: theme.colors.onSurface }}
							>
								{card.front.text}
							</Text>
							<Caption>Flip</Caption>
						</Box>
					</ScrollView>
				</Pressable>
			</Animated.View>
			<Animated.View
				style={[
					baseFaceStyle,
					backStyle,
					{ pointerEvents: isFlipped ? "auto" : "none" },
				]}
				accessibilityElementsHidden={!isFlipped}
				importantForAccessibility={isFlipped ? "auto" : "no-hide-descendants"}
			>
				{sections ? (
					<>
						<Box
							flexDirection="row"
							justifyContent="flex-end"
							alignItems="center"
							paddingHorizontal={12}
							paddingTop={8}
							paddingBottom={4}
						>
							<Pressable
								onPress={handleFlip}
								style={{ flexDirection: "row", alignItems: "center", gap: 4 }}
								accessibilityRole="button"
								accessibilityLabel="Flip card back"
							>
								<Icon
									source="rotate-3d-variant"
									size={16}
									color={theme.colors.primary}
								/>
								<Caption style={{ color: theme.colors.primary }}>
									Flip back
								</Caption>
							</Pressable>
						</Box>
						<Box flex={1}>
							<CustomTabs
								tabs={sections.map((s) => ({
									label: s.title,
									content: (
										<ScrollView
											contentContainerStyle={{ padding: 16 }}
											showsVerticalScrollIndicator={false}
										>
											<MarkdownRenderer
												text={s.content}
												variant="explanation"
												fontSize={14}
											/>
										</ScrollView>
									),
								}))}
								mode="fixed"
							/>
						</Box>
					</>
				) : (
					<>
						<Box
							flexDirection="row"
							justifyContent="space-between"
							alignItems="center"
							paddingHorizontal={16}
							paddingTop={12}
							paddingBottom={8}
							borderBottomWidth={1}
							borderBottomColor={theme.colors.outlineVariant}
						>
							<Caption
								style={{
									color: theme.colors.primary,
									fontWeight: "600",
									letterSpacing: 0.4,
								}}
							>
								{`CARD ${cardIndex + 1}`}
							</Caption>
							<Pressable
								onPress={handleFlip}
								style={{ flexDirection: "row", alignItems: "center", gap: 4 }}
								accessibilityRole="button"
								accessibilityLabel="Flip card back"
							>
								<Icon
									source="rotate-3d-variant"
									size={16}
									color={theme.colors.primary}
								/>
								<Caption style={{ color: theme.colors.primary }}>
									Flip back
								</Caption>
							</Pressable>
						</Box>
						<ScrollView
							style={{ flex: 1 }}
							contentContainerStyle={{
								paddingHorizontal: 20,
								paddingBottom: 16,
							}}
							showsVerticalScrollIndicator
						>
							<MarkdownRenderer
								text={card.back}
								variant="explanation"
								fontSize={14}
								customStyles={{ heading4: { fontSize: 14, marginBottom: 4 } }}
							/>
						</ScrollView>
					</>
				)}
			</Animated.View>
		</View>
	);
};

const Flashcards = ({ cards, itemId, onAllViewed }: Props) => {
	const theme = useAppTheme();
	const { isMobile } = useDevice();
	const { height: viewportHeight } = useWindowDimensions();
	const [index, setIndex] = useState(0);

	const cardWidth = isMobile ? 300 : 400;
	const reservedChrome = isMobile ? 320 : 280;
	const cardHeight = Math.max(
		300,
		Math.min(400, viewportHeight - reservedChrome),
	);

	// biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
	useEffect(() => {
		setIndex(0);
	}, [itemId]);

	useEffect(() => {
		if (index === cards.length - 1) {
			onAllViewed?.();
		}
	}, [index, cards.length, onAllViewed]);

	const isLast = index === cards.length - 1;

	return (
		<Box gap={20} alignItems="center" paddingTop={24}>
			<FlipCard
				key={`${itemId}-${index}`}
				card={cards[index]}
				cards={cards}
				cardIndex={index}
				cardWidth={cardWidth}
				cardHeight={cardHeight}
			/>

			<Box flexDirection="row" gap={20} alignItems="center">
				<Button
					mode="outlined"
					onPress={() => setIndex((i) => Math.max(0, i - 1))}
					disabled={index === 0}
					icon="chevron-left"
				>
					Prev
				</Button>
				<Caption style={{ minWidth: 80, textAlign: "center" }}>
					Card {index + 1} of {cards.length}
				</Caption>
				<Button
					mode="contained"
					onPress={() => setIndex((i) => Math.min(cards.length - 1, i + 1))}
					disabled={isLast}
					icon="chevron-right"
					contentStyle={{ flexDirection: "row-reverse" }}
				>
					Next card
				</Button>
			</Box>

			{isLast ? (
				<Box flexDirection="row" alignItems="center" gap={6}>
					<Icon source="check-circle" size={16} color={theme.colors.primary} />
					<Caption style={{ color: theme.colors.primary }}>
						Deck complete — click Next to continue
					</Caption>
				</Box>
			) : null}
		</Box>
	);
};

export default Flashcards;
